import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { EventType, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, filter, take } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { extractValueFromStore } from '@ninety/platform/services';
import {
  TeamListStateActions,
  CurrentUserSelectors,
  MazFeatureEnum,
  MazSharedActions,
  UserListStateActions,
  selectFeatureEnabled,
  selectUserFilterOptions,
} from '@ninety/platform/state';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import { TerraFormFieldModule, TerraIconModule, TerraOptionModule, TerraSelectModule } from '@ninety/terra';
import { NinetyTooltipDirective, ButtonComponent, NinetyChipComponent } from '@ninety/ui/core';

import { MeetingPrepFeedbackComponent } from '../meeting-prep-feedback/meeting-prep-feedback.component';

import { MeetingPrepPanelActions } from './meeting-prep-panel.actions';
import { ITEM_ENTITY_TYPE, MEETING_PREP_CTA_ID_BY_ITEM, MeetingPrepItemKey } from './meeting-prep-panel.model';
import { MeetingPrepPanelService } from './meeting-prep-panel.service';

const CTA_ID_PASCAL: Record<MeetingPrepItemKey, string> = {
  kpis: 'Scorecard',
  rocks: 'Rocks',
  headlines: 'Headlines',
  todos: 'Todos',
  issues: 'Issues',
};

const NAVIGATION_SETTLED = new Set<EventType>([
  EventType.NavigationEnd,
  EventType.NavigationSkipped,
  EventType.NavigationCancel,
  EventType.NavigationError,
]);

export const MAZ_PROMPT: Record<MeetingPrepItemKey, string> = {
  rocks:
    "Review my team's Rocks ahead of our next L10. Which are off-track or at risk, and what milestones are due or overdue? Help me check off completed milestones and flag off-track Rocks to raise as issues.",
  issues:
    'Help me build my issues list for our next L10 — what should I add to the IDS list? Help me create the issues.',
  todos:
    "Review my overdue and due-this-week to-dos ahead of our next L10. Help me mark done the ones I've completed and flag anything to discuss.",
  kpis: "Review my team's scorecard ahead of our next L10. Which measurables are off-track or trending the wrong way, and which should I flag to discuss? Help me enter any missing scores.",
  headlines:
    'Help me prepare my customer and employee headlines for our next L10 — anything worth sharing. Help me capture them.',
};

@Component({
  selector: 'ninety-meeting-prep-panel',
  templateUrl: './meeting-prep-panel.component.html',
  styleUrl: './meeting-prep-panel.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.meeting-prep-docked]': 'isDocked()' },
  imports: [
    ButtonComponent,
    FormsModule,
    MeetingPrepFeedbackComponent,
    NinetyChipComponent,
    NinetyTooltipDirective,
    RouterLink,
    TerraFormFieldModule,
    TerraIconModule,
    TerraOptionModule,
    TerraSelectModule,
  ],
})
export class MeetingPrepPanelComponent {
  private store = inject(Store);
  private panelService = inject(MeetingPrepPanelService);
  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private injector = inject(Injector);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Shared state lives in the service so the CTA entry point can hide while the panel is open.
  protected readonly isVisible = this.panelService.isVisible;
  protected readonly isExpanded = this.panelService.isExpanded;
  protected readonly selectedTeamId = signal<string | null>(null);

  protected readonly isDocked = computed(() => this.isVisible() && !this.isExpanded() && this.panelService.mazOpen());

  // The bar and expanded panel are mutually exclusive @if branches, so focus must move to the
  // freshly rendered controls after each view swap. Focus restore on full close lives in the CTA.
  private wasExpanded = false;

  private itemsClicked = 0;
  private pendingMazDispatch?: Subscription;
  private mazCtasShownForTeam: string | null = null;

  private readonly canAskMaz = toSignal(this.store.select(selectFeatureEnabled(Scopes.mazConversationalData)), {
    initialValue: false,
  });

  protected readonly teams = toSignal(this.store.select(CurrentUserSelectors.selectTeams), {
    initialValue: [],
  });

  protected readonly routes: Record<'rocks' | 'issues' | 'todos' | 'scorecard' | 'headlines', string[]> = {
    rocks: ['/rocks'],
    issues: ['/issues', 'short-term'],
    todos: ['/todos', 'team'],
    scorecard: ['/data', 'scorecards', 'weekly'],
    headlines: ['/headlines'],
  };

  constructor() {
    effect(() => {
      const request = this.panelService.teamRequest();
      untracked(() => {
        if (request) this.selectedTeamId.set(request.teamId);
      });
    });

    effect(() => {
      const expanded = this.isVisible() && this.isExpanded();
      if (expanded && !this.wasExpanded) {
        this.focusAfterRender('[aria-label="Minimize Meeting Prep"]');
        this.trackModalOpened();
      } else if (!expanded && this.wasExpanded && this.isVisible()) {
        this.focusAfterRender('[aria-label="Open Meeting Prep panel"]');
      }
      this.wasExpanded = expanded;
    });

    effect(() => {
      const isVisible = this.isVisible();
      const rowsVisible = isVisible && this.isExpanded();
      const teamId = this.selectedTeamId();
      const canAskMaz = this.canAskMaz();
      untracked(() => {
        // Resetting on collapse would re-fire the whole set on every row click.
        if (!isVisible) {
          this.mazCtasShownForTeam = null;
          return;
        }
        if (!rowsVisible || !teamId || !canAskMaz || this.mazCtasShownForTeam === teamId) return;
        this.mazCtasShownForTeam = teamId;
        this.store.dispatch(
          MeetingPrepPanelActions.mazCtasShown({ teamId, isOnMyNinety: this.panelService.surface() === 'my90' })
        );
      });
    });

    effect(() => {
      const teams = this.teams();
      untracked(() => {
        if (this.selectedTeamId() !== null || teams.length === 0) return;
        const user = extractValueFromStore(this.store, CurrentUserSelectors.selectUser);
        const match = teams.find(team => team._id === user?.settings?.defaultTeamId) ?? teams[0];
        this.selectedTeamId.set(match._id);
      });
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: KeyboardEvent): void {
    // Another surface (dialog, menu, overlay) already consumed this ESC.
    if (event.defaultPrevented) {
      return;
    }
    if (this.isExpanded()) {
      this.minimize();
    } else if (this.isVisible()) {
      this.close('esc');
    }
  }

  protected expand(): void {
    this.panelService.open();
  }

  protected minimize(): void {
    this.panelService.minimize();
  }

  protected close(reason: 'close_button' | 'esc' = 'close_button'): void {
    this.store.dispatch(
      SegmentActions.track({
        event: SegmentTrackEvent.RNA_MODAL_DISMISSED,
        // TODO(DEV-25721 follow-up): meetingId source TBD
        params: {
          isOnMyNinety: this.panelService.surface() === 'my90',
          meetingId: null,
          dismissReason: reason,
          itemsClickedBeforeDismiss: this.itemsClicked,
        },
      })
    );
    this.panelService.close();
  }

  protected onFeedbackRated(helpful: boolean): void {
    this.store.dispatch(
      SegmentActions.track({
        event: SegmentTrackEvent.RNA_FEEDBACK_RATED,
        params: { helpful, ...this.feedbackContext() },
      })
    );
  }

  protected onFeedbackSubmitted({ helpful, comment }: { helpful: boolean; comment: string }): void {
    this.store.dispatch(
      SegmentActions.track({
        event: SegmentTrackEvent.RNA_FEEDBACK_SUBMITTED,
        params: { helpful, comment: comment || undefined, ...this.feedbackContext() },
      })
    );
  }

  // TODO(DEV-25721 follow-up): meetingId source TBD
  private feedbackContext(): { isOnMyNinety: boolean; teamId: string | null; meetingId: null } {
    return { isOnMyNinety: this.panelService.surface() === 'my90', teamId: this.selectedTeamId(), meetingId: null };
  }

  protected onTeamChange(toTeamId: string): void {
    const fromTeamId = this.selectedTeamId();
    this.selectedTeamId.set(toTeamId);
    if (fromTeamId === toTeamId) return;
    this.store.dispatch(
      SegmentActions.track({
        event: SegmentTrackEvent.RNA_TEAM_CHANGED,
        // TODO(DEV-25721 follow-up): meetingId source TBD
        params: { isOnMyNinety: this.panelService.surface() === 'my90', meetingId: null, fromTeamId, toTeamId },
      })
    );
  }

  protected onItemClick(item: MeetingPrepItemKey, event?: MouseEvent): void {
    // RouterLink bails on modifier clicks, so no navigation follows.
    if (event && (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey)) return;
    const teamId = this.selectedTeamId();
    if (!teamId) return;
    this.itemsClicked++;
    const routeKey = item === 'kpis' ? 'scorecard' : item;
    // entityType categorizes the deep-link target; no entityId — this is navigation, not a specific instance.
    const entityType = ITEM_ENTITY_TYPE[item];
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(
      SegmentActions.track({
        event: SegmentTrackEvent.RNA_ITEM_CLICKED,
        // TODO(DEV-25721 follow-up): meetingId source TBD
        params: {
          item,
          entityType,
          isOnMyNinety: this.panelService.surface() === 'my90',
          meetingId: null,
          teamId,
          destinationUrl: this.routes[routeKey].join('/'),
        },
      })
    );
    if (this.canAskMaz()) {
      // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
      this.store.dispatch(
        MeetingPrepPanelActions.mazCtaClicked({
          ctaId: MEETING_PREP_CTA_ID_BY_ITEM[item],
          teamId,
          isOnMyNinety: this.panelService.surface() === 'my90',
        })
      );
    }
    this.dispatchTeamChange();
    if (item === 'rocks' || item === 'todos') {
      this.defaultOwnerFilterToCurrentUser();
    }
    if (this.canAskMaz()) this.openMazForItem(item, teamId);
  }

  private openMazForItem(item: MeetingPrepItemKey, teamId: string): void {
    const teamName = this.teams().find(team => team._id === teamId)?.name;
    const setFeature = MazSharedActions.setFeature({
      feature: MazFeatureEnum.conversationalDataQuery,
      metadata: teamName ? { additionalContext: `The current team is ${teamName}` } : {},
      userPrompt: MAZ_PROMPT[item],
      eventSource: `meetingPrep${CTA_ID_PASCAL[item]}`,
    });
    this.pendingMazDispatch?.unsubscribe();
    this.pendingMazDispatch = this.router.events
      .pipe(
        filter(event => NAVIGATION_SETTLED.has(event.type)),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.store.dispatch(setFeature));
    this.panelService.minimizeForMaz();
  }

  // 2 dispatches: the shared filter-bar reducer toggles, so clear 'all' then select the current user.
  private defaultOwnerFilterToCurrentUser(): void {
    const currentUser = extractValueFromStore(this.store, CurrentUserSelectors.selectUser);
    const allUsers = (extractValueFromStore(this.store, selectUserFilterOptions) ?? [])
      .map(option => option.item)
      .filter(user => user._id !== 'all');
    if (!currentUser || allUsers.length <= 1) return;
    this.store.dispatch(UserListStateActions.changeFilterBarUsers({ users: [], allUsers }));
    this.store.dispatch(UserListStateActions.changeFilterBarUsers({ users: [currentUser], allUsers }));
  }

  private focusAfterRender(selector: string): void {
    afterNextRender(() => this.elementRef.nativeElement.querySelector<HTMLElement>(selector)?.focus(), {
      injector: this.injector,
    });
  }

  private trackModalOpened(): void {
    this.itemsClicked = 0;
    untracked(() =>
      this.store.dispatch(
        SegmentActions.track({
          event: SegmentTrackEvent.RNA_MODAL_OPENED,
          // TODO(DEV-25721 follow-up): meetingId source TBD
          params: {
            isOnMyNinety: this.panelService.surface() === 'my90',
            meetingId: null,
            teamId: this.selectedTeamId(),
          },
        })
      )
    );
  }

  protected dispatchTeamChange(): void {
    const teamId = this.selectedTeamId();
    if (teamId) {
      this.store.dispatch(TeamListStateActions.changeFilterBarTeam({ id: teamId }));
    }
  }
}
